import React, { useEffect } from 'react'
import { useGetProfileQuery } from '../../../redux/apis/endpoints/profileApi'
import { useHistory } from 'react-router-dom'
import ProfileBackground from '../../../components/overview/ProfileBackground'
import ProfilePhoto from '../../../components/overview/ProfilePhoto'
import AboutMe from '../../../components/overview/AboutMe'
import ProfileStats from '../../../components/overview/ProfileStats'
import LoadingState from '../../../components/overview/LoadingState'
import Button from '../../../components/ui/Button'
import { PencilIcon, MapPinIcon } from '@heroicons/react/24/outline'
import { useSelector } from 'react-redux'
import { selectDefaultAddress } from '../../../redux/slices/addressSlice'
import { useGetAddressesQuery } from '../../../redux/apis'
import { useGetMyOrdersQuery, useGetMerchantOrdersQuery } from '../../../redux/apis/endpoints/orderApi'
import UserRatingDisplay from '../../../components/common/UserRatingDisplay'

// Get user language for localization
const userLang = navigator.language || navigator.userLanguage

/**
 * Overview component displays the user profile including:
 * - Profile background image
 * - Profile photo
 * - About me section with social links
 * - Statistics (products, transactions, followers, following)
 *
 * Uses the profileApi.js to fetch profile data
 */
const Overview = ({ userId, profileData }) => {
  const history = useHistory()
  const {
    data: currentUser,
    isLoading: isCurrentUserLoading,
    error
  } = useGetProfileQuery()

  const { data: myOrders = [], isLoading: isLoadingMyOrders } = useGetMyOrdersQuery();
  const { data: merchantOrders = [], isLoading: isLoadingMerchantOrders } = useGetMerchantOrdersQuery();
  const defaultAddress = useSelector(selectDefaultAddress)
  const { data: addresses = [] } = useGetAddressesQuery()
  const userToDisplay = profileData || currentUser;
  const isLoadingProfile = !profileData && isCurrentUserLoading;

  useEffect(() => {
    console.log('[Overview Debug] --- Status Update ---');
    console.log('[Overview Debug] isCurrentUserLoading:', isCurrentUserLoading);
    console.log('[Overview Debug] currentUser:', currentUser);
    console.log('[Overview Debug] userToDisplay:', userToDisplay);
  }, [currentUser, isCurrentUserLoading, userToDisplay]);

  const isLoading = isLoadingProfile || isLoadingMyOrders || isLoadingMerchantOrders;

  console.log('DEBUG Overview - User data:', userToDisplay);

  if (isLoading) {
    return <LoadingState />
  }

  if (error) {
    return (
      <div className='p-6 text-center text-red-500'>
        Error loading profile: {error.message}
      </div>
    )
  }

  if (!userToDisplay) {
    return <div className='p-6 text-center text-gray-500'>User not found</div>
  }

  const socialLinks = userToDisplay?.socialLinkHandle || {}
  const isCurrentUser = !userId || (currentUser && userId === currentUser.id)

  const handleEditProfile = () => {
    history.push('/dashboard/settings')
  }

  const myTransactionsCount = myOrders.length;
  const merchantTransactionsCount = merchantOrders.length;

  const editButton = isCurrentUser ? (
    <Button 
      onClick={handleEditProfile}
      variant='teal'
      className='flex items-center space-x-2 px-4 py-2 shadow-md hover:shadow-lg transition-shadow'
    >
      <PencilIcon className='h-5 w-5' />
      <span>{userLang === 'zh-CN' ? '编辑' : 'Edit'}</span>
    </Button>
  ) : null

  const resolvedDefaultAddress = defaultAddress || addresses.find(addr => addr.isDefault)

  return (
    <div className='space-y-6 relative'>
      <div className='relative'>
        <ProfileBackground user={userToDisplay} isCurrentUser={isCurrentUser} />
      </div>
      <div className='relative px-6'>
        <ProfilePhoto user={userToDisplay} isCurrentUser={isCurrentUser} />
        <AboutMe 
          user={userToDisplay} 
          socialLinks={socialLinks} 
          editButton={editButton}
        />
        {isCurrentUser && resolvedDefaultAddress && (
          <div className="mt-3 flex items-center text-sm text-gray-600">
            <MapPinIcon className="h-4 w-4 mr-1 text-brand-teal" />
            <span>{resolvedDefaultAddress.city}, {resolvedDefaultAddress.country}</span>
          </div>
        )}

        <UserRatingDisplay userId={userToDisplay?.id} ratingComponentSize="base" className="mt-3" />

      </div>
      <ProfileStats 
        user={userToDisplay} 
        myTransactionsCount={myTransactionsCount}
        merchantTransactionsCount={merchantTransactionsCount}
      />
    </div>
  )
}

export default Overview
